<template>
  <div :id="id">
    <div role="tablist" class="tab-list">
      <div class="highlight" :style="highlightStyle"></div>
      <button
        v-for="(tab, index) in tabs"
        :key="`${idPrefix}tab-${index}`"
        :id="tab.tabId"
        class="py-space-xxxs py-lg-space-xxs btn tab-button position-relative"
        :class="[{ 'active-tab-button': currentTab === index }, buttonClass]"
        role="tab"
        type="button"
        tabIndex="0"
        :aria-labelledby="`${idPrefix}tab-${index}`"
        :aria-controls="`${idPrefix}tabpanel-${index}`"
        :aria-selected="currentTab === index"
        :ref="($el) => setTabElementRef($el, index)"
        @click="handleTabClick($event, index)"
        :style="tabButtonStyles(index)">
        <div class="d-flex justify-content-between align-items-start">
          <slot :name="`icon(${index + 1})`"></slot>
        </div>
        {{ tab.tabName }}
      </button>
    </div>
    <div class="position-relative">
      <template v-for="(tab, index) in tabs" :key="tab.tabName">
        <Transition>
          <div
            :id="`${idPrefix}tabpanel-${index}`"
            v-if="index === currentTab"
            role="tabpanel"
            tabindex="0"
            class="tab-panel"
            :aria-labelledby="`${idPrefix}tab-${index}`">
            <p
              v-if="tabs[currentTab].title"
              class="my-space-sm tab-title"
              :class="titleClass">
              {{ tabs[currentTab].title }}
            </p>
            <div
              v-if="tabs[currentTab].text"
              v-html="tabs[currentTab].text"
              class="mb-space-sm fw-medium tab-text"></div>
            <slot :name="`tab(${index + 1})`"></slot>
          </div>
        </Transition>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementSize, type MaybeElement } from "@vueuse/core";
import {
  computed,
  nextTick,
  onMounted,
  reactive,
  ref,
  shallowRef,
  watch,
  type ComponentPublicInstance,
  type Ref,
} from "vue";

// Props interface
interface RequirementTab {
  tabId: string;
  tabName?: string;
  title?: string;
  text?: string;
}

interface Props {
  tabs: RequirementTab[];
  id: string;
  activeTabBackground?: string;
  titleSize?: "small" | "medium" | "large" | "xl";
  titleWeight?: "bold" | "normal" | "light" | "lighter";
  titleVariant?: string;
  buttonSize?: "sm" | "md" | "lg";
  buttonWeight?: "bold" | "normal" | "light" | "lighter";
  activeTabVariant?: string;
  inactiveTabVariant?: string;
}

// Define props with defaults
const props = withDefaults(defineProps<Props>(), {
  titleSize: "large",
  titleWeight: "bold",
  titleVariant: "dark-3",
  activeTabBackground: "primary",
  activeTabVariant: "white",
  inactiveTabVariant: "dark-1",
  buttonSize: "lg",
  buttonWeight: "bold",
});

// Emit events
const emits = defineEmits<{
  (e: "tabClick", event: Event, index: number): void;
}>();

// Function to handle element references and widths
const useFunctionRef = (
  refArray: Ref<(Element | ComponentPublicInstance | null)[]>
) => {
  const elementWidths = reactive<number[]>([]);
  const functionRef = (
    el: Element | ComponentPublicInstance | null,
    index: number
  ) => {
    if (!el) return; // Ensure element is available
    if (tabElements.value[index] === el) return;
    refArray.value[index] = el;
    const element = ref(el) as Ref<MaybeElement>;
    // Delay width calculation to allow styles to be fully applied
    setTimeout(() => {
      const { width } = useElementSize(element);
      elementWidths[index] = width.value;
      if (index === currentTab.value) {
        updateTabDimensions(); // Ensure we update active tab dimensions
      }
    }, 100);
  };

  return { functionRef, elementWidths };
};

// Reactive references
const tabElements = shallowRef<Array<HTMLElement | null> | []>([]);
const currentTab = ref(0);
const activeTabWidth = ref<number>(0);
const activeTabOffset = ref<number>(0);

// Function to set tab elements and their widths
const { functionRef: setTabElementRef, elementWidths } =
  useFunctionRef(tabElements);

// Computed styles
const activeTabBackgroundStyle = computed(() => {
  const activeBackground = props.activeTabBackground;
  return activeBackground && activeBackground !== "transparent"
    ? `var(--rds-${activeBackground})`
    : "transparent";
});

const idPrefix = computed(() =>
  props.id && props.id !== "" ? props.id + "-" : "default-"
);
const titleClass = computed(
  () =>
    `fs-${props.titleSize} fw-${props.titleWeight} text-${props.titleVariant}`
);
const buttonClass = computed(
  () => `fs-${props.buttonSize} fw-${props.buttonWeight}`
);

// Highlight style based on active tab
const highlightStyle = computed(() => ({
  width: `${activeTabWidth.value}px`,
  transform: `translateX(${activeTabOffset.value}px)`,
  backgroundColor: activeTabBackgroundStyle.value,
  transition: "all 0.3s ease", // Smooth transition
}));

// Tab button styles (active vs. inactive)
const tabButtonStyles = computed(() => (index: number) => ({
  color:
    currentTab.value === index
      ? `var(--rds-${props.activeTabVariant})`
      : `var(--rds-${props.inactiveTabVariant})`,
}));

// Handle tab click
const handleTabClick = (event: Event, index: number) => {
  currentTab.value = index;
  emits("tabClick", event, index);
};

const setCurrentTab = (index: number) => {
  currentTab.value = index;
};

// Function to update dimensions (called after mount and tab change)
const updateTabDimensions = () => {
  const activeIndex = currentTab.value;
  activeTabWidth.value = elementWidths[activeIndex] || 0;
  activeTabOffset.value = tabElements.value[activeIndex]?.offsetLeft || 0;
};

// Watchers

watch(
  currentTab,
  () => {
    nextTick(() => {
      updateTabDimensions();
    });
  },
  { immediate: true }
);

onMounted(() => {
  nextTick(() => {
    updateTabDimensions(); // Set initial tab dimensions after mount
  });
});

defineExpose<{ setActiveTab: (arg: number) => void }>({
  setActiveTab: setCurrentTab,
});
</script>

<style lang="scss" scoped>
.tab-list {
  border: 1px solid var(--rds-light-4, #d0d0d0);
  border-radius: 100px;
  padding: 8px;
  display: inline-flex;
  gap: 8px;
  position: relative;
}

.tab-button {
  padding: 8px 16px; // Use the same padding for both active and inactive tabs
  font-size: 18px;
  border-radius: 0;
  border: 0;
  box-sizing: border-box;
  margin: 0px;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  transition: 0.3s;

  &:not(.active-tab-button):hover {
    color: var(
      --rds-dark-3
    ) !important; // Change color to dark-3 on hover for inactive tabs
  }
}

.tab-button.active-tab-button {
  top: 0px;
  left: 0;
  bottom: 8px;
  border-radius: 100px;
  transition: 0.3s;
}

.highlight {
  position: absolute;
  top: 8px;
  left: 0;
  bottom: 8px;
  border-radius: 100px;
  transition: 0.3s;
  margin: 0;
}

.v-leave-active {
  position: absolute;
  top: 0;
  left: 0;
}
.v-enter-active {
  transition: opacity 0.175s ease;
}
.v-enter-from {
  opacity: 0;
}

// @media (min-width: 992px) {
//   .tab-list {
//     padding: 8px;
//   }

//   .highlight {
//     top: 8px;
//     bottom: 8px;
//   }
// }
</style>
